interface HeatmapTableProps {
  data: number[][]
  rowLabels: string[]
  colLabels: string[]
  formatter?: (value: number) => string
  colorScale?: 'diverging' | 'sequential'
  minValue?: number
  maxValue?: number
}

function getColor(value: number, min: number, max: number, colorScale: string): string {
  const normalized = max === min ? 0.5 : (value - min) / (max - min)

  if (colorScale === 'diverging') {
    // Blue (negative) → White (0) → Red (positive)
    if (normalized < 0.5) {
      const intensity = Math.round(255 * (1 - normalized * 2))
      return `rgb(${255 - intensity}, ${255 - intensity}, 255)`
    } else {
      const intensity = Math.round(255 * ((normalized - 0.5) * 2))
      return `rgb(255, ${255 - intensity}, ${255 - intensity})`
    }
  }

  // Sequential: white to primary blue
  const intensity = Math.round(200 * normalized)
  return `rgb(${220 - intensity}, ${230 - intensity}, 255)`
}

export default function HeatmapTable({
  data,
  rowLabels,
  colLabels,
  formatter = (v) => v.toFixed(2),
  colorScale = 'diverging',
  minValue,
  maxValue,
}: HeatmapTableProps) {
  const allValues = data.flat()
  const min = minValue ?? Math.min(...allValues)
  const max = maxValue ?? Math.max(...allValues)

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="px-2 py-1 text-left font-medium text-muted-foreground sticky left-0 bg-card"></th>
            {colLabels.map((label) => (
              <th key={label} className="px-2 py-1 text-center font-medium text-muted-foreground whitespace-nowrap">
                {label.length > 15 ? label.slice(0, 15) + '...' : label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={rowLabels[i]}>
              <td className="px-2 py-1 font-medium text-muted-foreground whitespace-nowrap sticky left-0 bg-card">
                {rowLabels[i].length > 20 ? rowLabels[i].slice(0, 20) + '...' : rowLabels[i]}
              </td>
              {row.map((value, j) => (
                <td
                  key={j}
                  className="px-2 py-1 text-center"
                  style={{ backgroundColor: getColor(value, min, max, colorScale) }}
                  title={`${rowLabels[i]} / ${colLabels[j]}: ${formatter(value)}`}
                >
                  {formatter(value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
